import { supabase } from "@/integrations/supabase/client";
import { INSTAGRAM_COMMENTS_SCOPE, launchInstagramOAuth } from "@/lib/instagramOAuth";

// "Reply to comments with a DM" readiness for the dashboard. Mirrors
// supabase/functions/_shared/connectors/instagram/webhookSubscription.ts
// (instagramCommentsReady) and commentDm.ts (commentDmSettingsReady).

/** Comments reach Dibs once Meta accepted the comments subscription and the
 *  token can carry the permission. A connection made before Dibs asked for it
 *  has no `grantedScopes` at all and needs a reconnect. */
export function instagramCommentsReady(config: unknown): boolean {
  if (!config || typeof config !== "object") return false;
  const { commentsSubscribed, grantedScopes } = config as {
    commentsSubscribed?: unknown;
    grantedScopes?: unknown;
  };
  if (commentsSubscribed !== true) return false;
  if (Array.isArray(grantedScopes)) return grantedScopes.includes(INSTAGRAM_COMMENTS_SCOPE);
  return grantedScopes === null;
}

export type InstagramCommentsConnection = "not_connected" | "needs_reconnect" | "ready";

export async function fetchInstagramCommentsConnection(
  businessId: string,
): Promise<InstagramCommentsConnection> {
  const { data, error } = await (supabase as any)
    .from("business_channel_connections")
    .select("status, config")
    .eq("business_id", businessId)
    .eq("channel", "instagram")
    .eq("status", "connected")
    .maybeSingle();
  if (error) throw error;
  if (!data) return "not_connected";
  return instagramCommentsReady(data.config) ? "ready" : "needs_reconnect";
}

interface CommentDmConfig {
  keywords?: unknown;
  dm_enabled?: unknown;
  dm_message?: unknown;
  comment_reply_enabled?: unknown;
  comment_reply?: unknown;
}

/** What still stops the workflow from doing anything, or null when it can
 *  run. Same defaults as the backend: DM on, public reply off. */
export function commentDmSetupProblem(config: CommentDmConfig | null | undefined): string | null {
  const keywords = Array.isArray(config?.keywords)
    ? config.keywords.filter((k) => typeof k === "string" && k.trim())
    : [];
  if (keywords.length === 0) return "Add at least one trigger keyword.";
  const dmOn = config?.dm_enabled !== false;
  const replyOn = config?.comment_reply_enabled === true;
  const hasDm = dmOn && typeof config?.dm_message === "string" && !!config.dm_message.trim();
  const hasReply = replyOn && typeof config?.comment_reply === "string" && !!config.comment_reply.trim();
  if (!hasDm && !hasReply) return "Write the DM people get when they comment.";
  return null;
}

/** Why "Reply to comments with a DM" can't be turned on yet, or null. */
export async function commentDmTurnOnBlocker(
  businessId: string,
): Promise<{ title: string; description: string } | null> {
  const [connection, settings] = await Promise.all([
    fetchInstagramCommentsConnection(businessId),
    (supabase as any)
      .from("workflow_message_settings")
      .select("config")
      .eq("business_id", businessId)
      .eq("workflow_id", "instagram-comment-dm")
      .maybeSingle(),
  ]);
  if (connection === "not_connected") {
    return {
      title: "Connect Instagram first",
      description: "Go to Settings, then Connections, and connect your Instagram account.",
    };
  }
  if (connection === "needs_reconnect") {
    return {
      title: "Reconnect Instagram first",
      description: "Dibs needs permission to read comments. Go to Settings, then Connections, and reconnect Instagram.",
    };
  }
  const problem = commentDmSetupProblem(settings.data?.config);
  return problem ? { title: "Finish setting it up first", description: problem } : null;
}

/** Reconnect Instagram asking for the comments permission too. Same flow as
 *  the Connections card; the backend records what Instagram granted. */
export async function reconnectInstagramForComments(): Promise<void> {
  const { code, redirectUri } = await launchInstagramOAuth({ comments: true });
  const { data, error } = await supabase.functions.invoke("instagram-onboard", {
    body: { action: "connect_oauth", code, redirectUri },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.detail || data.error);
}
